export type FloorPlanOptimizationProgress = {
  phase: string
  label: string
  detail?: string
  stepIndex: number
  totalSteps: number
  progressPct: number
}

export const OPTIMIZATION_PHASES = [
  { id: 'inventory', label: 'Reading inventory & floor model' },
  { id: 'geometry', label: 'Checking walls and obstacles' },
  { id: 'baseline', label: 'Simulating baseline RF coverage' },
  { id: 'recommendations', label: 'Applying RF recommendations' },
  { id: 'placement', label: 'Finding best mounting positions' },
  { id: 'direction', label: 'Tuning wall direction & mount height' },
  { id: 'channels', label: 'Assigning channels & cutting interference' },
  { id: 'power', label: 'Balancing TX power for coverage' },
  { id: 'gaps', label: 'Scanning coverage gaps' },
  { id: 'finalize', label: 'Finalizing optimized plan' },
] as const

export const OPTIMIZATION_TOTAL_STEPS = OPTIMIZATION_PHASES.length

/** Minimum time to show optimization progress (ms). */
export const MIN_OPTIMIZATION_DURATION_MS = 5000

/** Minimum time to show the saving phase (ms). */
export const MIN_SAVING_DURATION_MS = 800

export function uiYield(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function makeOptimizationProgress(
  phase: (typeof OPTIMIZATION_PHASES)[number]['id'],
  detail?: string,
): FloorPlanOptimizationProgress {
  const stepIndex = OPTIMIZATION_PHASES.findIndex(p => p.id === phase) + 1
  const phaseDef = OPTIMIZATION_PHASES[stepIndex - 1]
  return {
    phase,
    label: phaseDef.label,
    detail,
    stepIndex,
    totalSteps: OPTIMIZATION_TOTAL_STEPS,
    progressPct: Math.round((stepIndex / OPTIMIZATION_TOTAL_STEPS) * 100),
  }
}

/** Keep each step visible long enough that the full run is ≥ MIN_OPTIMIZATION_DURATION_MS. */
export async function paceOptimizationStep(
  stepIndex: number,
  startedAt: number,
  onProgress: ((progress: FloorPlanOptimizationProgress) => void) | undefined,
  phase: (typeof OPTIMIZATION_PHASES)[number]['id'],
  detail?: string,
  work?: () => void,
): Promise<void> {
  onProgress?.(makeOptimizationProgress(phase, detail))
  work?.()
  const targetMs = (stepIndex / OPTIMIZATION_TOTAL_STEPS) * MIN_OPTIMIZATION_DURATION_MS
  const wait = Math.max(60, targetMs - (Date.now() - startedAt))
  await uiYield(wait)
}

export async function paceSavingPhase(startedAt: number): Promise<void> {
  const wait = Math.max(0, MIN_SAVING_DURATION_MS - (Date.now() - startedAt))
  if (wait > 0) await uiYield(wait)
}
